import { CQAppConstant } from 'app-constants';

/**
 * Next UTC run time of the daily scheduler after `after`. Single source of
 * truth for the cutoff; other helpers below derive from it.
 */
export const getNextSchedulerRunUtc = (after: Date = new Date()): Date => {
    const { HOUR, MINUTE } = CQAppConstant.PUSH_PORTAL_UI.SCHEDULER_RUN_UTC;
    const candidate = new Date(Date.UTC(after.getUTCFullYear(), after.getUTCMonth(), after.getUTCDate(), HOUR, MINUTE, 0, 0));
    return candidate.getTime() > after.getTime() ? candidate : new Date(candidate.getTime() + 24 * 60 * 60 * 1000);
};

/**
 * Minimum selectable UTC date (`YYYY-MM-DD`) for the date picker — today if
 * the daily cutoff hasn't passed, otherwise tomorrow.
 */
export const getMinSelectableUtcDate = (): string => {
    return getNextSchedulerRunUtc().toISOString().slice(0, 10);
};

/**
 * Builds the ISO `scheduledTime` from a picked `YYYY-MM-DD` date, fixed to
 * the scheduler's daily run time in UTC.
 */
export const buildScheduledTimeIso = (dateStr: string): string => {
    // Enforce YYYY-MM-DD first: Number('') is 0, not NaN, so empty/malformed input would otherwise silently become a "valid" year-1900 date.
    if (!CQAppConstant.PUSH_PORTAL_UI.DATE_ONLY_REGEX.test(dateStr || '')) return '';
    const { HOUR, MINUTE } = CQAppConstant.PUSH_PORTAL_UI.SCHEDULER_RUN_UTC;
    const [year, month, day] = dateStr.split('-').map(Number);
    const candidate = new Date(Date.UTC(year, month - 1, day, HOUR, MINUTE, 0, 0));
    // Return '' on invalid input so callers' Number.isNaN(new Date(...).getTime()) checks catch it.
    return Number.isNaN(candidate.getTime()) ? '' : candidate.toISOString();
};

/**
 * Extracts the UTC date (`YYYY-MM-DD`) from an ISO timestamp for prefilling
 * the date-only input; falls back to the minimum selectable date.
 */
export const formatDateOnlyInput = (dateValue?: string): string => {
    if (!dateValue) return getMinSelectableUtcDate();
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return getMinSelectableUtcDate();
    return date.toISOString().slice(0, 10);
};

/**
 * Formats a `scheduledTime` (or `YYYY-MM-DD` string) as a human-readable date
 * with time in UTC format. Forces UTC so the displayed day/time matches what was
 * picked, regardless of viewer timezone. Returns '' on absent/invalid input.
 */
export const formatScheduledDateDisplay = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        hour: 'numeric', 
        minute: 'numeric', 
        timeZone: 'UTC',
        timeZoneName: 'short'
    });
};
